/**
 * TanStack Query hooks for authentication data
 *
 * These hooks provide race-condition-free data fetching for:
 * - User profile
 * - User roles (tenant-scoped)
 * - Tenant count
 * - Tenant membership
 *
 * Benefits over manual state management:
 * - Automatic request deduplication
 * - Automatic abort on key change (prevents stale data)
 * - Built-in caching with staleTime
 * - Automatic retries with backoff
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";

// ============================================================================
// Types
// ============================================================================

// ADR-CHK-002: super_admin consolidated into admin.
// TES-1833 / GOAL A1 (v2.3): qm_admin is a canonical backbone role resolved via role_id
// (roles.key='qm_admin', mig 320/321) — deliberately NOT an app_role enum value (281/327 forbid
// the enum path). fetchRoles() below surfaces it from the joined roles row, so the frontend role
// list (and useAuth.isQmAdmin) can see role_id-only roles the bare enum column never carries.
export type AppRole =
  | "admin"
  | "tester"
  | "qualitaetsmanager"
  | "mangelbearbeiter"
  | "qm_admin"
  | "customer_approver";

// Runtime allowlist of the canonical backbone roles. fetchRoles surfaces role_id-only roles via the
// roles(key) embed, but migration 325 also seeds OTHER role_id-only is_system roles (testify_admin,
// mangelerfasser, mangelzuweiser, read_only, sammel_pdf) whose keys are NOT AppRoles. Validate the
// embedded key against this set so only canonical roles enter roles: AppRole[] — mirroring the DB
// is_qm_admin() helper's key='qm_admin' narrowing, and keeping consumers (permissions/navConfig) safe.
const APP_ROLES = [
  "admin",
  "tester",
  "qualitaetsmanager",
  "mangelbearbeiter",
  "qm_admin",
  "customer_approver",
] as const satisfies readonly AppRole[];
const APP_ROLE_SET: ReadonlySet<string> = new Set(APP_ROLES);
function isAppRole(key: string): key is AppRole {
  return APP_ROLE_SET.has(key);
}

// External-identity category (ADR 0008 / mig 286). null for internal users.
export type ExternalCategory = "partner_supplier" | "external_auditor" | "customer_acceptance";

export interface Profile {
  id: string;
  user_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  avatar_url: string | null;
  active_tenant_id: string | null;
  active_team_id: string | null;
  registration_approved: boolean;
  /**
   * External-identity flags (ADR 0008, mig 286). For Entra B2B guests / external
   * partners these drive the anchor-bound read-only UI (TES-626). Authorization is
   * still enforced server-side by RLS — these are display signals only.
   */
  external_user: boolean;
  entra_oid: string | null;
  external_category: ExternalCategory | null;
  created_at: string;
  updated_at: string;
}

// Internal-only — the public membership type lives in @/types/admin. Not exported (avoid a dead
// public export); used solely as fetchTenantMembership's return shape below.
interface TenantMembership {
  tenant_id: string;
  is_tenant_admin: boolean;
}

// ============================================================================
// Query Keys Factory
// ============================================================================

export const authKeys = {
  all: ["auth"] as const,
  profile: (userId: string) => ["auth", "profile", userId] as const,
  roles: (userId: string, tenantId: string | null) => ["auth", "roles", userId, tenantId] as const,
  tenantCount: (userId: string) => ["auth", "tenantCount", userId] as const,
  tenantMembership: (userId: string, tenantId: string | null) =>
    ["auth", "membership", userId, tenantId] as const,
};

// ============================================================================
// Query Functions
// ============================================================================

async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select(
      "id, user_id, email, first_name, last_name, display_name, avatar_url, active_tenant_id, active_team_id, registration_approved, external_user, entra_oid, external_category, created_at, updated_at",
    )
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    logger.error("Error fetching profile:", error);
    throw error;
  }

  return data as unknown as Profile | null;
}

// Shape of the embedded roles row (user_roles.role_id → roles). PostgREST may return a
// to-one embed as an object or, depending on inference, a single-element array — handle both.
type EmbeddedRole = { key: string; is_active: boolean };
type UserRoleRow = { role: AppRole | null; roles?: EmbeddedRole | EmbeddedRole[] | null };

function embeddedRole(
  roles: EmbeddedRole | EmbeddedRole[] | null | undefined,
): EmbeddedRole | undefined {
  return Array.isArray(roles) ? roles[0] : (roles ?? undefined);
}

/**
 * Resolve one user_roles assignment to its canonical role key, or null if it grants nothing.
 *
 * The joined `roles` row is authoritative and mirrors the DB predicates (mig 320 joins
 * `roles ON … AND ro.is_active`): an INACTIVE role grants nothing — for BOTH role_id-only roles
 * (qm_admin, whose `app_role` enum column is NULL) AND the 6 enum-backed roles (which set both).
 * Only canonical AppRole keys surface — role_id-only non-backbone roles (testify_admin,
 * mangelerfasser, …) are dropped so the typed list never carries an invalid key (mirrors
 * is_qm_admin's narrowing). Only when no row is embedded do we fall back to the bare enum column.
 */
function resolveRoleKey(r: UserRoleRow): AppRole | null {
  const row = embeddedRole(r.roles);
  if (!row) return r.role;
  return row.is_active && isAppRole(row.key) ? row.key : null;
}

async function fetchRoles(userId: string, activeTenantId: string | null): Promise<AppRole[]> {
  // Embed the joined roles row so role_id-only roles (qm_admin — mig 320/321) become visible:
  // their app_role enum column is NULL, the canonical key lives in roles.key (TES-1833 / GOAL A1).
  let query = supabase
    .from("user_roles")
    .select("role, roles(key, is_active)")
    .eq("user_id", userId);

  // If active tenant is set, only load roles for that tenant + global roles (super_admin)
  if (activeTenantId) {
    query = query.or(`tenant_id.eq.${activeTenantId},tenant_id.is.null`);
  }

  const { data, error } = await query;

  if (error) {
    logger.error("Error fetching roles:", error);
    throw error;
  }

  // flatMap drops assignments that resolve to nothing (deactivated / unresolvable) — no null entries.
  return (
    data?.flatMap((r) => {
      const key = resolveRoleKey(r as UserRoleRow);
      return key ? [key] : [];
    }) ?? []
  );
}

async function fetchTenantCount(userId: string): Promise<number> {
  const { data, error } = await supabase.rpc("get_user_tenant_count", {
    _user_id: userId,
  });

  if (error) {
    logger.error("Error fetching tenant count:", error);
    throw error;
  }

  if (data === null || data === undefined) throw new Error("get_user_tenant_count returned null");
  return data;
}

async function fetchTenantMembership(
  userId: string,
  tenantId: string,
): Promise<TenantMembership | null> {
  const { data, error } = await supabase
    .from("tenant_memberships")
    .select("tenant_id, is_tenant_admin")
    .eq("user_id", userId)
    .eq("tenant_id", tenantId)
    .maybeSingle();

  if (error) {
    logger.error("Error fetching tenant membership:", error);
    throw error;
  }

  return data;
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch user profile with TanStack Query
 * Automatically aborts on user change to prevent race conditions
 */
export function useAuthProfile(userId: string | null) {
  const queryClient = useQueryClient();
  useRealtimeSubscription(
    "profiles",
    () => queryClient.invalidateQueries({ queryKey: authKeys.profile(userId ?? "") }),
    { enabled: !!userId, debounceMs: 1000 },
  );

  return useQuery({
    queryKey: authKeys.profile(userId ?? ""),
    queryFn: () => fetchProfile(userId!),
    enabled: !!userId,
    staleTime: 30_000, // 30s - Profile changes rarely
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
    retry: 2,
  });
}

/**
 * Fetch user roles for active tenant with TanStack Query
 * Re-fetches when tenantId changes
 */
export function useAuthRoles(userId: string | null, tenantId: string | null) {
  const queryClient = useQueryClient();
  useRealtimeSubscription(
    "user_roles",
    () => queryClient.invalidateQueries({ queryKey: authKeys.roles(userId ?? "", tenantId) }),
    { enabled: !!userId, debounceMs: 1000 },
  );

  return useQuery({
    queryKey: authKeys.roles(userId ?? "", tenantId),
    queryFn: () => fetchRoles(userId!, tenantId),
    enabled: !!userId,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Fetch tenant count for user
 */
export function useAuthTenantCount(userId: string | null) {
  const queryClient = useQueryClient();
  useRealtimeSubscription(
    "tenant_memberships",
    () => queryClient.invalidateQueries({ queryKey: authKeys.tenantCount(userId ?? "") }),
    { enabled: !!userId, debounceMs: 1000 },
  );

  return useQuery({
    queryKey: authKeys.tenantCount(userId ?? ""),
    queryFn: () => fetchTenantCount(userId!),
    enabled: !!userId,
    staleTime: 60_000, // 1 minute - tenant count changes very rarely
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}

/**
 * Fetch tenant membership for user in active tenant
 */
export function useAuthTenantMembership(userId: string | null, tenantId: string | null) {
  const queryClient = useQueryClient();
  useRealtimeSubscription(
    "tenant_memberships",
    () =>
      queryClient.invalidateQueries({
        queryKey: authKeys.tenantMembership(userId ?? "", tenantId),
      }),
    { enabled: !!userId && !!tenantId, debounceMs: 1000 },
  );

  return useQuery({
    queryKey: authKeys.tenantMembership(userId ?? "", tenantId),
    queryFn: () => fetchTenantMembership(userId!, tenantId!),
    enabled: !!userId && !!tenantId,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: 2,
  });
}
